/**
 * Priority List Report Page
 * 
 * The authoritative cycle-level product priorities report.
 * Shows ratified priorities with full governance trail.
 * 
 * Purpose: "What are our current priorities, and why is this the right list?"
 * 
 * Design Invariant: The Cycle Report reads Session Results, Loop Roll-Ups,
 * Priority List states, and Override events. It does not calculate them.
 */

import { CycleVersionFlags } from '../../../components/prioritization/CycleVersionFlags';
import { ObjectRef, ObjectKindLabel } from '@/components/ds/ObjectRef';
import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft,
  Shield,
  Clock,
  Target,
  ListOrdered,
  CheckCircle,
  AlertTriangle,
  Info,
  ArrowRight,
  FileText,
  Award,
  Lock,
  Globe,
  ExternalLink,
  ArrowUpDown,
} from 'lucide-react';
import { usePrioritySetStore } from '../../../stores/prioritization/prioritySetStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { ItemRelationshipFlags } from '@/components/prioritization/ItemRelationshipFlags';
import {
  CHIP_OVERFLOW_CLASS,
  CLASSIFICATION_CHIP_CLASS,
  SOURCE_CONTEXT_CHIP_CLASS,
} from '../../../lib/classificationDisplay';
import {
  PRIORITY_SET_STATE_ORDER,
  type GovernanceEvent,
  type PriorityOverride,
} from '../../../types/prioritization/prioritySet';

// ============================================================================
// Types
// ============================================================================

interface GovernanceStep {
  state: string;
  label: string;
  icon: React.ElementType;
  isComplete: boolean;
  isCurrent: boolean;
  record?: {
    actor: string;
    date?: Date;
    note?: string;
  };
}

// ============================================================================
// Component
// ============================================================================

export function PrioritySetReport() {
  const { prioritySetId } = useParams<{ prioritySetId: string }>();
  
  // Get priority list
  const prioritySets = usePrioritySetStore(state => state.prioritySets);
  const fetchPrioritySets = usePrioritySetStore(state => state.fetchFromSupabase);
  const prioritySet = useMemo(
    () => prioritySets.find(ps => ps.id === prioritySetId),
    [prioritySets, prioritySetId]
  );
  // Opened by its address (a shared link, a reload, the item page's links): load the
  // sets ourselves instead of relying on another page having loaded them first.
  const [loadChecked, setLoadChecked] = useState(false);
  const isKnown = Boolean(prioritySet);
  useEffect(() => {
    if (isKnown) return;
    let live = true;
    void fetchPrioritySets().finally(() => live && setLoadChecked(true));
    return () => {
      live = false;
    };
  }, [isKnown, prioritySetId, fetchPrioritySets]);
  
  // Get cycle info
  const cycles = useDegapProcessStore(state => state.cycles);
  const cycle = useMemo(
    () => prioritySet?.cycleId ? cycles.find(c => c.id === prioritySet.cycleId) : undefined,
    [cycles, prioritySet?.cycleId]
  );
  
  // Get loop info
  const loops = useCatchballLoopStore(state => state.loops);
  const sourceLoop = useMemo(
    () => prioritySet?.loopId ? loops.find(l => l.id === prioritySet.loopId) : undefined,
    [loops, prioritySet?.loopId]
  );
  
  // Get session results
  const allItemPools = useItemPoolsStore(state => state.itemPools);
  const includedItemPools = useMemo(() => {
    if (!prioritySet) return [];
    return allItemPools.filter(fl => prioritySet.itemPoolIds.includes(fl.id));
  }, [allItemPools, prioritySet]);

  const classificationSummaryTags = useMemo(() => {
    return Array.from(
      new Set(
        includedItemPools.flatMap((itemPool) => itemPool.tags?.filter(Boolean) ?? [])
      )
    ).slice(0, 4);
  }, [includedItemPools]);

  const itemContextById = useMemo(() => {
    const context = new Map<string, { tags: string[]; sourceListNames: string[] }>();

    includedItemPools.forEach((itemPool) => {
      itemPool.priorityIds.forEach((itemId) => {
        const existing = context.get(itemId);
        const tags = itemPool.tags?.filter(Boolean) ?? [];

        if (!existing) {
          context.set(itemId, { tags, sourceListNames: [itemPool.name] });
          return;
        }

        context.set(itemId, {
          tags: Array.from(new Set([...existing.tags, ...tags])),
          sourceListNames: existing.sourceListNames.includes(itemPool.name)
            ? existing.sourceListNames
            : [...existing.sourceListNames, itemPool.name],
        });
      });
    });

    return context;
  }, [includedItemPools]);
  
  // Get items for item details
  const items = useItemsStore(state => state.items);
  const getCandidateTitle = (itemId: string) => {
    const item = items.find(c => c.id === itemId);
    return item?.title || 'Unknown Item';
  };
  
  // Build governance steps
  const governanceSteps: GovernanceStep[] = useMemo(() => {
    if (!prioritySet) return [];
    
    const stateIndex = PRIORITY_SET_STATE_ORDER.indexOf(prioritySet.state);
    
    return [
      {
        state: 'proposed',
        label: 'Proposed',
        icon: FileText,
        isComplete: stateIndex >= 0,
        isCurrent: prioritySet.state === 'proposed',
        record: {
          actor: prioritySet.proposedBy ?? 'Unknown',
          date: prioritySet.proposedAt,
        },
      },
      {
        state: 'decided',
        label: 'Decided',
        icon: CheckCircle,
        isComplete: stateIndex >= 1,
        isCurrent: prioritySet.state === 'decided',
        record: prioritySet.decision ? {
          actor: prioritySet.decision.decidedBy,
          date: prioritySet.decision.decidedAt,
          note: prioritySet.decision.note,
        } : undefined,
      },
      {
        state: 'ratified',
        label: 'Ratified',
        icon: Lock,
        isComplete: stateIndex >= 2,
        isCurrent: prioritySet.state === 'ratified',
        record: prioritySet.ratification ? {
          actor: prioritySet.ratification.ratifiedBy,
          date: prioritySet.ratification.ratifiedAt,
          note: prioritySet.ratification.note,
        } : undefined,
      },
      {
        state: 'published',
        label: 'Published',
        icon: Globe,
        isComplete: stateIndex >= 3,
        isCurrent: prioritySet.state === 'published',
        record: prioritySet.publication ? {
          actor: prioritySet.publication.publishedBy,
          date: prioritySet.publication.publishedAt,
          note: prioritySet.publication.message,
        } : undefined,
      },
    ];
  }, [prioritySet]);
  
  // Get all overrides
  const allOverrides = useMemo(() => {
    if (!prioritySet) return [];
    return prioritySet.ratification?.overrides || [];
  }, [prioritySet]);
  
  if (!prioritySet && !loadChecked) {
    return <div className="px-4 py-12 text-sm text-[var(--text-ter)]">Loading the priority list…</div>;
  }

  // Priority list not found
  if (!prioritySet) {
    return (
      <div className="px-4 py-12">
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-[var(--coral)] mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-[var(--coral)] mb-2">Priority List Not Found</h1>
          <p className="text-[var(--coral)] mb-4">
            The Priority List with ID "{prioritySetId}" does not exist or has been deleted.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/prioritization/reports/cycles"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700"
            >
              See cycle reports
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/prioritization/reports"
              className="inline-flex items-center gap-2 text-[var(--coral)] hover:text-[var(--coral)]"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to Reports
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  const finalRankedItemIds = prioritySet.ratification?.finalRankedItemIds || prioritySet.rankedItemIds;
  const cutLinePosition = prioritySet.ratification?.cutLinePosition || prioritySet.cutLinePosition;
  
  return (
    <div className="px-6 py-6">
      {/* Items this list's cycle ranked on different versions (928) — shown until settled, never blocking. */}
      {prioritySet.cycleId && <CycleVersionFlags cycleId={prioritySet.cycleId} className="mb-4" />}
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/prioritization/reports"
          className="inline-flex items-center text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-2"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Reports
        </Link>
        
        <div className="flex items-start justify-between">
          <div>
            <ObjectKindLabel kind="priority_set" />
            <h1 className="text-2xl font-bold text-[var(--text-pri)]">{prioritySet.name}</h1>
            <p className="text-[var(--text-sec)] mt-1">
              {cycle?.name || 'Unknown Cycle'} - Priority List Report
            </p>
            {classificationSummaryTags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {classificationSummaryTags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex rounded-full bg-[var(--hover-tint)] px-2.5 py-1 text-xs text-[var(--text-sec)]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              prioritySet.state === 'published' 
                ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                : prioritySet.state === 'ratified'
                ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                : prioritySet.state === 'decided'
                ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
                : 'bg-[var(--hover-tint)] text-[var(--text-pri)]'
            }`}>
              {prioritySet.state.charAt(0).toUpperCase() + prioritySet.state.slice(1)}
            </span>
          </div>
        </div>
      </div>
      
      {/* Governance Progress */}
      <section className="bg-[var(--bg-raised)] border border-[var(--rule)] mb-6">
        <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-[var(--text-sec)]" />
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">Governance Trail</h2>
          </div>
          <p className="text-sm text-[var(--text-ter)] mt-1">
            Proposed → Decided → Ratified → Published
          </p>
        </div>
        
        <div className="p-6">
          <div className="flex items-center justify-between">
            {governanceSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={step.state} className="flex items-center">
                  {/* Step */}
                  <div className={`flex flex-col items-center ${
                    step.isCurrent ? 'opacity-100' : step.isComplete ? 'opacity-100' : 'opacity-40'
                  }`}>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      step.isCurrent
                        ? 'bg-primary-500 text-white'
                        : step.isComplete
                        ? 'bg-[var(--green)] text-white'
                        : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
                    }`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <span className={`text-sm font-medium mt-2 ${
                      step.isCurrent ? 'text-primary-700' : step.isComplete ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)]'
                    }`}>
                      {step.label}
                    </span>
                    {step.record?.date && (
                      <span className="text-xs text-[var(--text-ter)] mt-0.5">
                        {step.record.date.toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  
                  {/* Connector */}
                  {index < governanceSteps.length - 1 && (
                    <div className={`flex-1 h-1 mx-4 rounded ${
                      governanceSteps[index + 1].isComplete || governanceSteps[index + 1].isCurrent
                        ? 'bg-[var(--green)]'
                        : 'bg-[var(--hover-tint)]'
                    }`} style={{ minWidth: '60px' }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--slate-dim)]">
              <ListOrdered className="h-5 w-5 text-[var(--text-ter)]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--text-pri)]">{finalRankedItemIds.length}</p>
              <p className="text-sm text-[var(--text-ter)]">Items</p>
            </div>
          </div>
        </div>
        
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--green-dim)]">
              <Target className="h-5 w-5 text-[var(--green)]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--text-pri)]">{includedItemPools.length}</p>
              <p className="text-sm text-[var(--text-ter)]">Session Results</p>
            </div>
          </div>
        </div>
        
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--mustard-dim)]">
              <ArrowUpDown className="h-5 w-5 text-[var(--mustard)]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--text-pri)]">{allOverrides.length}</p>
              <p className="text-sm text-[var(--text-ter)]">Overrides</p>
            </div>
          </div>
        </div>
        
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--slate-dim)]">
              <Clock className="h-5 w-5 text-[var(--text-ter)]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--text-pri)]">{prioritySet.events.length}</p>
              <p className="text-sm text-[var(--text-ter)]">Events</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Section 1: Ratified Priority Ordering */}
      <section className="bg-[var(--bg-raised)] border border-[var(--rule)] mb-6">
        <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-[var(--text-sec)]" />
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">
              Ratified Priority Ordering ({finalRankedItemIds.length} items)
            </h2>
          </div>
          {cutLinePosition !== undefined && (
            <p className="text-sm text-[var(--mustard)] mt-1">
              Cut line at position {cutLinePosition} - {cutLinePosition} items are prioritized
            </p>
          )}
        </div>
        
        {finalRankedItemIds.length === 0 ? (
          <div className="p-6 text-center">
            <Info className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-2" />
            <p className="text-sm text-[var(--text-ter)]">No items in this priority list.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[var(--rule)]">
              <thead className="bg-[var(--surface)]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase">
                    Item
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase">
                    Classification / Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-ter)] uppercase">
                    Overridden?
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)]">
                {finalRankedItemIds.map((itemId, index) => {
                  const isAboveCutLine = cutLinePosition === undefined || index < cutLinePosition;
                  const isCutLine = cutLinePosition !== undefined && index === cutLinePosition;
                  const override = allOverrides.find(o => o.itemId === itemId);
                  const itemContext = itemContextById.get(itemId);
                  
                  return (
                    <tr
                      key={itemId}
                      className={`${isCutLine ? 'border-t-4 border-[var(--coral)]' : ''} ${
                        !isAboveCutLine ? 'bg-[var(--surface)] opacity-60' : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                          index < 3 ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                        }`}>
                          {index + 1}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-[var(--text-pri)]">
                          {getCandidateTitle(itemId)}
                        </div>
                        <ItemRelationshipFlags itemId={itemId} className="mt-1" />
                      </td>
                      <td className="px-6 py-4">
                        {itemContext ? (
                          <div className="space-y-2">
                            {itemContext.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {itemContext.tags.slice(0, 3).map((tag) => (
                                  <span
                                    key={`${itemId}-${tag}`}
                                    className={CLASSIFICATION_CHIP_CLASS}
                                  >
                                    {tag}
                                  </span>
                                ))}
                                {itemContext.tags.length > 3 && (
                                  <span
                                    className={CHIP_OVERFLOW_CLASS}
                                    title={itemContext.tags.join(', ')}
                                  >
                                    +{itemContext.tags.length - 3} more
                                  </span>
                                )}
                              </div>
                            )}
                            <div className="flex flex-wrap gap-1.5">
                              <span className={SOURCE_CONTEXT_CHIP_CLASS} title={itemContext.sourceListNames.join(', ')}>
                                {itemContext.sourceListNames[0]}
                              </span>
                              {itemContext.sourceListNames.length > 1
                                ? (
                                  <span className={CHIP_OVERFLOW_CLASS} title={itemContext.sourceListNames.join(', ')}>
                                    +{itemContext.sourceListNames.length - 1} more list{itemContext.sourceListNames.length - 1 === 1 ? '' : 's'}
                                  </span>
                                )
                                : null}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-[var(--text-ter)]">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isAboveCutLine ? (
                          <span className="inline-flex items-center gap-1 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                            <CheckCircle className="h-4 w-4" />
                            Prioritized
                          </span>
                        ) : (
                          <span className="text-sm text-[var(--text-ter)]">Below cut line</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {override ? (
                          <span className="inline-flex items-center gap-1 text-sm text-[var(--mustard)]">
                            <ArrowUpDown className="h-4 w-4" />
                            {override.type}
                          </span>
                        ) : (
                          <span className="text-sm text-[var(--text-ter)]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      
      {/* Section 2: Override Rationale */}
      <section className="bg-[var(--bg-raised)] border border-[var(--rule)] mb-6">
        <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <ArrowUpDown className="h-5 w-5 text-[var(--text-sec)]" />
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">
              Override Rationale ({allOverrides.length})
            </h2>
          </div>
          <p className="text-sm text-[var(--text-ter)] mt-1">
            Changes made between governance states with required rationale
          </p>
        </div>
        
        {allOverrides.length === 0 ? (
          <div className="p-6 text-center">
            <CheckCircle className="h-8 w-8 text-[var(--green)] mx-auto mb-2" />
            <p className="text-sm text-[var(--text-ter)]">
              No overrides were applied. The final ranking matches the proposed output.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--rule)]">
            {allOverrides.map(override => (
              <OverrideCard
                key={override.id}
                override={override}
                getCandidateTitle={getCandidateTitle}
              />
            ))}
          </div>
        )}
      </section>
      
      {/* Section 3: Governance Event History */}
      <section className="bg-[var(--bg-raised)] border border-[var(--rule)] mb-6">
        <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-[var(--text-sec)]" />
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">
              Event History ({prioritySet.events.length})
            </h2>
          </div>
        </div>
        
        {prioritySet.events.length === 0 ? (
          <div className="p-6 text-center">
            <Info className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-2" />
            <p className="text-sm text-[var(--text-ter)]">No events recorded.</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--rule)]">
            {prioritySet.events
              .slice()
              .sort((a, b) => b.occurredAt.getTime() - a.occurredAt.getTime())
              .map(event => (
                <EventCard key={event.id} event={event} />
              ))}
          </div>
        )}
      </section>
      
      {/* Section 4: Source Links */}
      <section className="bg-[var(--bg-raised)] border border-[var(--rule)]">
        <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <ExternalLink className="h-5 w-5 text-[var(--text-sec)]" />
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">Source Links</h2>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {/* Source Loop */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[var(--text-pri)]">Source Loop</p>
              <p className="text-sm text-[var(--text-ter)]">
                {sourceLoop ? `Loop ${sourceLoop.loopNumber}` : 'Unknown Loop'}
              </p>
            </div>
            {sourceLoop && (
              <Link
                to={`/prioritization/reports/loop/${sourceLoop.id}`}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                View Loop Report →
              </Link>
            )}
          </div>
          
          {/* Cycle */}
          <div className="flex items-center justify-between border-t border-[var(--rule)] pt-4">
            <div>
              <p className="text-sm font-medium text-[var(--text-pri)]">Parent Cycle</p>
              <p className="text-sm">{cycle ? <ObjectRef kind="cycle" id={cycle.id} name={cycle.name} /> : <span className="text-[var(--text-ter)]">Unknown Cycle</span>}</p>
            </div>
            {cycle && (
              <Link
                to={`/prioritization/cycles/${cycle.id}`}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                View Cycle →
              </Link>
            )}
          </div>
          
          {/* Session Results */}
          {includedItemPools.length > 0 && (
            <div className="border-t border-[var(--rule)] pt-4">
              <p className="text-sm font-medium text-[var(--text-pri)] mb-2">Included Session Results</p>
              <div className="space-y-2">
                {includedItemPools.map(fl => (
                  <div key={fl.id} className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-[var(--text-sec)]">{fl.name}</span>
                      {fl.tags && fl.tags.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {fl.tags.slice(0, 3).map((tag) => (
                            <span
                              key={`${fl.id}-${tag}`}
                              className={CLASSIFICATION_CHIP_CLASS}
                            >
                              {tag}
                            </span>
                          ))}
                          {fl.tags.length > 3 && (
                            <span className={CHIP_OVERFLOW_CLASS} title={fl.tags.join(', ')}>
                              +{fl.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <Link
                      to={`/prioritization/lists/${fl.id}`}
                      className="text-sm text-primary-600 hover:text-primary-700"
                    >
                      View →
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      
      {/* Report Footer */}
      <div className="mt-6 text-center text-xs text-[var(--text-ter)]">
        <p>
          Priority List Report - The authoritative governance artifact.
        </p>
        <p className="mt-1">
          Priority List ID: {prioritySet.id}
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Override Card Component
// ============================================================================

interface OverrideCardProps {
  override: PriorityOverride;
  getCandidateTitle: (itemId: string) => string;
}

function OverrideCard({ override, getCandidateTitle }: OverrideCardProps) {
  return (
    <div className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`p-2  ${
            override.type === 'promote' ? 'bg-[var(--green-dim)]' :
            override.type === 'demote' ? 'bg-[var(--coral-dim)]' :
            'bg-[var(--mustard-dim)]'
          }`}>
            <ArrowUpDown className={`h-4 w-4 ${
              override.type === 'promote' ? 'text-[var(--green)]' :
              override.type === 'demote' ? 'text-[var(--coral)]' :
              'text-[var(--mustard)]'
            }`} />
          </div>
          <div>
            <p className="text-sm font-medium text-[var(--text-pri)]">
              {getCandidateTitle(override.itemId)}
            </p>
            <p className="text-sm text-[var(--text-sec)] mt-1">
              <span className="capitalize font-medium">{override.type}</span>
              {override.previousRank !== undefined && override.newRank !== undefined && (
                <span className="text-[var(--text-ter)]">
                  {' '}- Moved from #{override.previousRank} to #{override.newRank}
                </span>
              )}
            </p>
          </div>
        </div>
        <span className="text-xs text-[var(--text-ter)]">
          {override.duringTransition.replace(/_/g, ' ')}
        </span>
      </div>
      
      <div className="mt-3 ml-11">
        <p className="text-sm text-[var(--text-sec)] bg-[var(--surface)] p-2 rounded italic">
          "{override.rationale}"
        </p>
        <p className="text-xs text-[var(--text-ter)] mt-2">
          By {override.overriddenBy} on {override.overriddenAt.toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Event Card Component
// ============================================================================

interface EventCardProps {
  event: GovernanceEvent;
}

function EventCard({ event }: EventCardProps) {
  const getEventIcon = () => {
    switch (event.type) {
      case 'created': return FileText;
      case 'decided': return CheckCircle;
      case 'ratified': return Lock;
      case 'published': return Globe;
      case 'override_added': return ArrowUpDown;
      case 'superseded': return AlertTriangle;
      default: return Info;
    }
  };
  
  const getEventColor = () => {
    switch (event.type) {
      case 'created': return 'bg-[var(--hover-tint)] text-[var(--text-sec)]';
      case 'decided': return 'bg-[var(--mustard-dim)] text-[var(--mustard)]';
      case 'ratified': return 'bg-[var(--slate-dim)] text-[var(--text-sec)]';
      case 'published': return 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]';
      case 'override_added': return 'bg-[var(--mustard-dim)] text-[var(--mustard)]';
      case 'superseded': return 'bg-[var(--coral-dim)] text-[var(--coral)]';
      default: return 'bg-[var(--hover-tint)] text-[var(--text-sec)]';
    }
  };
  
  const Icon = getEventIcon();
  
  return (
    <div className="p-4 flex items-start gap-3">
      <div className={`p-2  ${getEventColor()}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-[var(--text-pri)] capitalize">
            {event.type.replace(/_/g, ' ')}
          </p>
          <span className="text-xs text-[var(--text-ter)]">
            {event.occurredAt.toLocaleDateString()} at {event.occurredAt.toLocaleTimeString()}
          </span>
        </div>
        <p className="text-sm text-[var(--text-ter)] mt-1">
          By {event.actorName || event.actorId}
        </p>
        {event.note && (
          <p className="text-sm text-[var(--text-sec)] mt-2 italic">
            "{event.note}"
          </p>
        )}
        {event.fromState && event.toState && (
          <p className="text-xs text-[var(--text-ter)] mt-1">
            {event.fromState} → {event.toState}
          </p>
        )}
      </div>
    </div>
  );
}

export default PrioritySetReport;
